// Sprint 8 — client-side attachment plumbing for the composer.
//
// Mirrors the backend dispatcher's route logic so the composer can:
//   1. predict which route a file will take (chip icon + label).
//   2. enforce the same per-file caps client-side BEFORE upload.
//   3. cap the per-message totals (image-count, pdf-count, total inline bytes).
//
// The truth is server-side; this file is for UX (immediate rejection toast,
// chip preview rendering). Any check skipped here will still fire at the
// backend's processAttachment().
//
// Limit constants must stay in sync with backend/src/attachments/dispatcher.ts.
// They live as plain consts so tests + components can import them.

export const ATT_LIMITS = {
  IMAGE_MAX_BYTES: 5 * 1024 * 1024,
  IMAGE_MAX_DIM_PX: 8000,
  IMAGE_MAX_PER_MSG: 20,
  PDF_MAX_BYTES: 32 * 1024 * 1024,
  PDF_MAX_PAGES: 100,
  PDF_MAX_PER_MSG: 5,
  TEXT_INLINE_MAX_BYTES: 1 * 1024 * 1024,
  TOTAL_INLINE_MAX_BYTES: 32 * 1024 * 1024,
  ATTACHMENTS_PER_MSG: 40,
} as const;

export type ChipRoute = 'image' | 'pdf' | 'text-inline' | 'disk';

export type ChipState = 'pending' | 'uploaded' | 'failed';

export interface AttachmentChipModel {
  id: string; // client-generated uuid
  messageId: string; // shared per-send uuid for grouping on backend
  file: File;
  displayName: string;
  size: number;
  mimeType: string;
  predictedRoute: ChipRoute;
  state: ChipState;
  // 0..1, only meaningful when state === 'pending'.
  progress: number;
  // Server-confirmed route + final attachment id (after upload).
  serverRoute?: ChipRoute;
  // Founder-mode display label (e.g. "Imagen — captura.png · 1.2 MB").
  label: string;
  errorMessage?: string;
  // AbortController so reload + retry can cancel in-flight uploads.
  abortController?: AbortController;
}

export interface ChipRejection {
  file: File;
  reason: string;
}

const IMAGE_MIMES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
const PDF_MIME = 'application/pdf';
const TEXT_LIKE_MIMES = new Set([
  'application/json',
  'application/xml',
  'application/javascript',
  'application/x-yaml',
  'application/x-ndjson',
  'application/x-ipynb+json',
]);

export function predictRoute(mime: string, size: number): ChipRoute {
  if (IMAGE_MIMES.has(mime) && size <= ATT_LIMITS.IMAGE_MAX_BYTES) return 'image';
  if (mime === PDF_MIME && size <= ATT_LIMITS.PDF_MAX_BYTES) return 'pdf';
  if ((mime.startsWith('text/') || TEXT_LIKE_MIMES.has(mime)) && size < ATT_LIMITS.TEXT_INLINE_MAX_BYTES) {
    return 'text-inline';
  }
  return 'disk';
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function routeNounEs(route: ChipRoute): string {
  switch (route) {
    case 'image':
      return 'Imagen';
    case 'pdf':
      return 'PDF';
    case 'text-inline':
      return 'Texto';
    case 'disk':
      return 'Adjunto';
  }
}

function buildLabel(displayName: string, size: number, route: ChipRoute): string {
  return `${routeNounEs(route)} — ${displayName} · ${formatBytes(size)}`;
}

export function describeAttachment(att: { name: string; size: number; route: ChipRoute }): string {
  return buildLabel(att.name, att.size, att.route);
}

export interface ChipPolicyState {
  imageCount: number;
  pdfCount: number;
  totalInlineBytes: number;
  totalCount: number;
}

export function emptyPolicyState(): ChipPolicyState {
  return { imageCount: 0, pdfCount: 0, totalInlineBytes: 0, totalCount: 0 };
}

/**
 * Try to admit `file` into a chip slot. Returns either a fresh chip + the
 * updated policy state, or a rejection with a founder-mode reason. Pure.
 *
 * `messageId` is the per-send uuid the composer manages. It is shared
 * across every chip in the same draft so the backend groups them under
 * the same .asoasis-uploads/<msg>/ dir.
 */
export function admitFile(
  file: File,
  state: ChipPolicyState,
  messageId: string,
  ids: { newId: () => string },
): { chip: AttachmentChipModel; nextState: ChipPolicyState } | { rejected: ChipRejection } {
  if (state.totalCount >= ATT_LIMITS.ATTACHMENTS_PER_MSG) {
    return {
      rejected: {
        file,
        reason: `Máximo ${ATT_LIMITS.ATTACHMENTS_PER_MSG} adjuntos por mensaje.`,
      },
    };
  }
  const mime = file.type || 'application/octet-stream';
  // Image cap (size + count).
  if (IMAGE_MIMES.has(mime)) {
    if (file.size > ATT_LIMITS.IMAGE_MAX_BYTES) {
      return {
        rejected: { file, reason: 'Imagen demasiado grande — máximo 5 MB (Claude API limit).' },
      };
    }
    if (state.imageCount >= ATT_LIMITS.IMAGE_MAX_PER_MSG) {
      return {
        rejected: {
          file,
          reason: `Máximo ${ATT_LIMITS.IMAGE_MAX_PER_MSG} imágenes por mensaje (Claude limit).`,
        },
      };
    }
  }
  // PDF cap (size + count). Pages are validated server-side via pdf-lib.
  if (mime === PDF_MIME) {
    if (file.size > ATT_LIMITS.PDF_MAX_BYTES) {
      return {
        rejected: { file, reason: 'PDF demasiado grande — máximo 32 MB (Claude API limit).' },
      };
    }
    if (state.pdfCount >= ATT_LIMITS.PDF_MAX_PER_MSG) {
      return {
        rejected: {
          file,
          reason: `Máximo ${ATT_LIMITS.PDF_MAX_PER_MSG} PDFs por mensaje (Claude limit).`,
        },
      };
    }
  }

  const route = predictRoute(mime, file.size);
  // Inline-byte cap: rough — base64 inflation is ~1.33×.
  const inlineBytes = route === 'image' || route === 'pdf' ? Math.ceil(file.size * 1.34) : 0;
  if (state.totalInlineBytes + inlineBytes > ATT_LIMITS.TOTAL_INLINE_MAX_BYTES) {
    return {
      rejected: {
        file,
        reason: 'Adjuntos demasiado pesados — divide en varios mensajes.',
      },
    };
  }

  const id = ids.newId();
  const chip: AttachmentChipModel = {
    id,
    messageId,
    file,
    displayName: file.name,
    size: file.size,
    mimeType: mime,
    predictedRoute: route,
    state: 'pending',
    progress: 0,
    label: buildLabel(file.name, file.size, route),
  };
  return {
    chip,
    nextState: {
      imageCount: state.imageCount + (IMAGE_MIMES.has(mime) ? 1 : 0),
      pdfCount: state.pdfCount + (mime === PDF_MIME ? 1 : 0),
      totalInlineBytes: state.totalInlineBytes + inlineBytes,
      totalCount: state.totalCount + 1,
    },
  };
}

/**
 * Walk a DataTransferItemList from a drop event and return only files
 * (not folders). Folders are silently dropped — the caller surfaces a
 * single toast for the whole event (ATT4).
 */
export async function filesFromDataTransfer(
  items: DataTransferItemList,
): Promise<{ files: File[]; rejectedFolderCount: number }> {
  const files: File[] = [];
  let rejectedFolderCount = 0;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item) continue;
    if (item.kind !== 'file') continue;
    type EntryShim = { isDirectory?: boolean };
    const itemWithEntry = item as DataTransferItem & {
      webkitGetAsEntry?: () => EntryShim | null;
    };
    const entry = itemWithEntry.webkitGetAsEntry?.();
    if (entry?.isDirectory) {
      rejectedFolderCount += 1;
      continue;
    }
    const f = item.getAsFile();
    if (f) files.push(f);
  }
  return { files, rejectedFolderCount };
}

/**
 * Walk a ClipboardEvent's items and pull every file (paste-from-screenshot,
 * paste-from-explorer, etc.). Plain-text pastes are filtered out — the
 * caller relies on the default browser behavior to insert the text.
 */
export function filesFromClipboard(items: DataTransferItemList | null): File[] {
  if (!items) return [];
  const files: File[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item || item.kind !== 'file') continue;
    const f = item.getAsFile();
    if (f) files.push(f);
  }
  return files;
}

/**
 * Decode just enough of an image to read its natural width/height, then
 * reject if either dimension exceeds Anthropic's vision cap (8000 px).
 * Without this the upload passes our checks, hits the API, and comes
 * back as an opaque 400 from a turn the founder already kicked off.
 *
 * Uses `createImageBitmap(file)` rather than `<img src="blob:...">` so
 * the dimension probe is NOT subject to the production CSP's `img-src`
 * directive (deploy/Caddyfile, web/public/_headers) — that policy
 * intentionally excludes `blob:` from `img-src`, which would silently
 * fail every legitimate upload here. PR #179's first cut used the
 * blob:// approach and bricked all image uploads in production.
 *
 * Resolves to `{ ok: true }` when the image is in-range, or to
 * `{ rejected: <founder-mode reason> }` when oversized. Decode failures
 * fail open: the server-side magic-byte check (verifyMagicBytes) is the
 * authoritative gate against malformed image bytes — we don't want to
 * reject a real image just because an exotic codec confused
 * createImageBitmap. The caller only invokes this for image MIMEs.
 */
export async function checkImageDimensions(
  file: File,
): Promise<{ ok: true } | { rejected: string }> {
  if (typeof createImageBitmap !== 'function') {
    return { ok: true };
  }
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return { ok: true };
  }
  const w = bitmap.width;
  const h = bitmap.height;
  bitmap.close?.();
  if (w > ATT_LIMITS.IMAGE_MAX_DIM_PX || h > ATT_LIMITS.IMAGE_MAX_DIM_PX) {
    return {
      rejected: `Imagen demasiado grande (${w}×${h}). Máximo ${ATT_LIMITS.IMAGE_MAX_DIM_PX}×${ATT_LIMITS.IMAGE_MAX_DIM_PX} px (Claude API limit).`,
    };
  }
  return { ok: true };
}
